import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  TextField,
  Paper,
  IconButton,
  InputAdornment,
  Alert,
  Link as MuiLink,
} from "@mui/material";
import { Visibility, VisibilityOff, ArrowBack } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import configuration from "../config";
import { useDispatch, useSelector } from "react-redux";
import { setCredentials } from "../features/counter/Authslice.js";
import { jwtDecode } from "jwt-decode";

const OTP_EXPIRY_TIME = 300; 
const MAX_OTP_ATTEMPTS = 3;

const LoginComponent = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const { API_BASE_URL } = configuration;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [otpTimer, setOtpTimer] = useState(OTP_EXPIRY_TIME);
  const [canResendOtp, setCanResendOtp] = useState(false);
  const [otpAttempts, setOtpAttempts] = useState(0);

 
  const { isAuthenticated } = useSelector((state) => state.auth);

  const { control, handleSubmit, getValues, setValue } = useForm({
    defaultValues: {
      email: "",
      password: "",
      otp: "",
    },
  });

 
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from || "/KycManagement";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

 
  useEffect(() => {
    let interval;
    if (otpStep && otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => {
          if (prev <= 1) {
            setCanResendOtp(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpStep, otpTimer]);


  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };


  const processAuthToken = useCallback(
    (token) => {
      try {
      
        sessionStorage.setItem("accessToken", token);

    
        const decodedToken = jwtDecode(token);
        const user = {
          id: decodedToken.id,
          email: decodedToken.email,
          role: decodedToken.role,
          username: decodedToken.username || null,
        };

      
        dispatch(
          setCredentials({
            token,
            user,
            isAuthenticated: true,
          })
        );

      
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

       
        const from = location.state?.from || "/KycManagement";
        navigate(from, { replace: true });
      } catch (error) {
        console.error("Error processing token:", error);
        throw new Error("Failed to process authentication token");
      }
    },
    [dispatch, navigate, location]
  );

 
  const handleSocialLoginSuccess = useCallback(
    (token) => {
      try {
        processAuthToken(token);
      } catch (error) {
        setError("Failed to process authentication. Please try again.");
      }
    },
    [processAuthToken]
  );

 
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const token = query.get("token");

    if (token) {
      handleSocialLoginSuccess(token);
    }
  }, [location, handleSocialLoginSuccess]);

 
  const handleRequestOTP = async (data) => {
    if (loading) return;

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/request-otp`,
        { email: data.email },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );

      if (response.data?.success) {
        setUserEmail(data.email);
        setOtpStep(true);
        setOtpTimer(OTP_EXPIRY_TIME);
        setCanResendOtp(false);
        setOtpAttempts(0);
        setValue("otp", "");
        setSuccess("OTP sent successfully to your email!");
      } else {
        throw new Error("Failed to send OTP");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to send OTP. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  const handleResendOTP = async () => {
    if (!canResendOtp || loading) return;

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/request-otp`,
        { email: userEmail },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );

      if (response.data?.success) {
        setOtpTimer(OTP_EXPIRY_TIME);
        setCanResendOtp(false);
        setOtpAttempts(0);
        setValue("otp", "");
        setSuccess("New OTP sent successfully!");
      }
    } catch (error) {
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  const handleVerifyOTP = async (data) => {
    if (loading) return;


    if (otpAttempts >= MAX_OTP_ATTEMPTS) {
      setError("Maximum OTP attempts exceeded. Please request a new OTP.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/verify-otp`,
        {
          email: userEmail,
          password: getValues("password"),
          otp: data.otp,
        },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );

      
      let token;
      if (response.data?.tokens) {
        token = response.data.tokens.accessToken;
      
        if (response.data.tokens.refreshToken) {
          localStorage.setItem("refreshToken", response.data.tokens.refreshToken);
        }
      } else if (response.data?.token) {
        token = response.data.token;
      } else {
        throw new Error("No authentication token received");
      }

   
      processAuthToken(token);
    } catch (error) {
      setOtpAttempts((prev) => prev + 1);

      if (error.response) {
        const backendMessage =
          error.response.data?.message || error.response.data?.error;
        const statusCode = error.response.status;

        // Handle specific error cases
        if (statusCode === 401) {
          setError("Invalid email or password.");
        } else if (backendMessage?.toLowerCase().includes("expired")) {
          setError("Your OTP has expired. Please request a new one.");
          setCanResendOtp(true);
          setOtpTimer(0);
        } else if (backendMessage?.toLowerCase().includes("otp")) {
          const remainingAttempts = MAX_OTP_ATTEMPTS - otpAttempts - 1;
          setError(
            `Invalid OTP. ${remainingAttempts} attempt${
              remainingAttempts !== 1 ? "s" : ""
            } remaining.`
          );
        } else if (statusCode === 429) {
          setError("Too many attempts. Please try again later.");
        } else {
          setError(backendMessage || "Login failed. Please try again.");
        }
      } else if (error.request) {
        setError("No response from server. Please check your connection.");
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Go back to email/password step
  const handleBackToLogin = () => {
    setOtpStep(false);
    setError("");
    setSuccess("");
    setOtpTimer(OTP_EXPIRY_TIME);
    setCanResendOtp(false);
    setOtpAttempts(0);
    setValue("otp", "");
  };

  return (
    <Container
      maxWidth="xs"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        minHeight: "100vh",
        py: 4,
      }}
    >
      <Paper
        elevation={12}
        sx={{
          p: 4,
          borderRadius: 3,
          textAlign: "center",
        }}
        component={motion.div}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: "medium", mb: 1 }}>
            Welcome back. We're glad to see you again.
          </Typography>
        </Box>

        <Typography variant="h4" gutterBottom>
          {otpStep ? "Verify OTP" : "Login"}
        </Typography>

      
        {otpStep && (
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackToLogin}
            sx={{ mb: 2 }}
            size="small"
          >
            Back to Login
          </Button>
        )}

      
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

       
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
            {success}
          </Alert>
        )}

        {!otpStep ? (
          <>
            {/* Email & Password Form */}
            <form onSubmit={handleSubmit(handleRequestOTP)}>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Email"
                    variant="outlined"
                    margin="normal"
                    error={!!error}
                    helperText={error?.message}
                    autoComplete="email"
                    autoFocus
                  />
                )}
              />

              <Controller
                name="password"
                control={control}
                rules={{
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    margin="normal"
                    error={!!error}
                    helperText={error?.message}
                    autoComplete="current-password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            aria-label="toggle password visibility"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

             
              <Box sx={{ textAlign: "right", mb: 1 }}>
                <MuiLink
                  component={Link}
                  to="/forgot-password"
                  variant="body2"
                  sx={{ textDecoration: "none" }}
                >
                  Forgot Password?
                </MuiLink>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 2,
                  py: 1.5,
                  color: "white",
                  backgroundColor: "#EB5A3C",
                  "&:hover": {
                    backgroundColor: "#d14d2f",
                  },
                  "&:disabled": {
                    backgroundColor: "#f5a691",
                    color: "rgba(255, 255, 255, 0.7)",
                  },
                }}
                disabled={loading}
              >
                {loading ? "Requesting OTP..." : "Get OTP"}
              </Button>
            </form>
          </>
        ) : (
          <>
            
            <form onSubmit={handleSubmit(handleVerifyOTP)}>
              <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                Enter the 4-digit OTP sent to <strong>{userEmail}</strong>
              </Typography>

              <Controller
                name="otp"
                control={control}
                rules={{
                  required: "OTP is required",
                  pattern: {
                    value: /^\d{4}$/,
                    message: "OTP must be 4 digits",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Enter OTP"
                    variant="outlined"
                    margin="normal"
                    error={!!error}
                    helperText={error?.message}
                    inputProps={{
                      maxLength: 4,
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    autoFocus
                    autoComplete="one-time-code"
                  />
                )}
              />

             
              <Box sx={{ mt: 1, mb: 2 }}>
                {otpTimer > 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    OTP expires in: <strong>{formatTime(otpTimer)}</strong>
                  </Typography>
                ) : (
                  <Typography variant="body2" color="error">
                    OTP has expired
                  </Typography>
                )}
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 2,
                  py: 1.5,
                  color: "white",
                  backgroundColor: "#EB5A3C",
                  "&:hover": {
                    backgroundColor: "#d14d2f",
                  },
                  "&:disabled": {
                    backgroundColor: "#f5a691",
                    color: "rgba(255, 255, 255, 0.7)",
                  },
                }}
                disabled={loading || otpAttempts >= MAX_OTP_ATTEMPTS}
              >
                {loading ? "Verifying..." : "Verify & Login"}
              </Button>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Didn't receive OTP?{" "}
                  <MuiLink
                    component="button"
                    type="button"
                    onClick={handleResendOTP}
                    disabled={!canResendOtp || loading}
                    sx={{
                      textDecoration: "none",
                      cursor: canResendOtp && !loading ? "pointer" : "not-allowed",
                      color: canResendOtp && !loading ? "primary.main" : "text.disabled",
                      "&:hover": {
                        textDecoration:
                          canResendOtp && !loading ? "underline" : "none",
                      },
                    }}
                  >
                    Resend OTP
                  </MuiLink>
                </Typography>
              </Box>
            </form>
          </>
        )}

        
        <Typography variant="body2" sx={{ mt: 3 }}>
          Don't have an account?{" "}
          <MuiLink
            component={Link}
            to="/signup"
            sx={{
              textDecoration: "none",
              fontWeight: "medium",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Sign Up
          </MuiLink>
        </Typography>
      </Paper>
    </Container>
  );
};

export default LoginComponent;
